import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Inbox, MessageSquare, RefreshCw, ShieldCheck, Edit3 } from "lucide-react";
import QuoteChatRoom from "@/components/QuoteChatRoom";
import QuoteEditor from "@/components/QuoteEditor";
import { toast } from "sonner";

const fmt = (v, c = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: c }).format(v || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Reviews() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openChatId, setOpenChatId] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);

  const allowed = !!(user?.is_admin || user?.is_fortinet_reviewer);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reviews/queue");
      setQueue(r.data || []);
    } catch (e) {
      toast.error("Échec du chargement de la file d'attente");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && allowed) refresh();
  }, [authLoading, allowed, refresh]);

  // Auto-poll every 15 s so reviewers see new requests without reloading
  useEffect(() => {
    if (!allowed) return;
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [allowed, refresh]);

  if (authLoading) return <div className="p-8 text-sm text-zinc-500">Chargement…</div>;
  if (!user) {
    nav("/login");
    return null;
  }
  if (!allowed) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-5 text-sm text-amber-900" data-testid="reviews-forbidden">
          Cette page est réservée aux Reviewers Fortinet. Demandez à un administrateur de vous attribuer le rôle.
        </div>
      </div>
    );
  }

  const openEditor = async (quoteId) => {
    try {
      const r = await api.get(`/quotes/${quoteId}`);
      setEditingQuote(r.data);
    } catch (e) {
      toast.error("Impossible de charger le devis");
    }
  };

  const requested = queue.filter((q) => q.review_status === "requested");
  const resolved = queue.filter((q) => q.review_status === "resolved");

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="reviews-page">
      <div className="flex items-start sm:items-center justify-between mb-6 flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#D42B1E]/10 flex items-center justify-center">
            <Inbox className="h-5 w-5 text-[#D42B1E]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Revues Fortinet</h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              Devis qui attendent une validation ou des corrections d'un ingénieur Fortinet.
            </p>
          </div>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" className="rounded-sm" data-testid="reviews-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      {/* Pending queue */}
      <Section title="En attente" count={requested.length} accent="amber">
        {requested.length === 0 ? (
          <Empty msg="Aucune revue en attente. Tout est à jour." />
        ) : (
          <Table
            rows={requested}
            onChat={setOpenChatId}
            onEdit={openEditor}
            highlightUnreplied
            currentUserId={user.id}
          />
        )}
      </Section>

      {/* Resolved history */}
      <Section title="Historique" count={resolved.length} accent="green" className="mt-8">
        {resolved.length === 0 ? (
          <Empty msg="Pas encore de revues clôturées." />
        ) : (
          <Table rows={resolved} onChat={setOpenChatId} onEdit={openEditor} currentUserId={user.id} />
        )}
      </Section>

      {/* Chat sheet */}
      <Sheet open={!!openChatId} onOpenChange={(v) => !v && setOpenChatId(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[560px] p-0 flex flex-col border-l-0 sm:border-l-4 sm:border-l-[#D42B1E]"
          data-testid="reviews-chat-sheet"
        >
          <SheetTitle className="sr-only">Salon Fortinet</SheetTitle>
          {openChatId && (
            <QuoteChatRoom
              quoteId={openChatId}
              quote={queue.find((q) => q.id === openChatId)}
              onReviewStatusChange={() => refresh()}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Inline editor (uses existing QuoteEditor) */}
      {editingQuote && (
        <Sheet open={!!editingQuote} onOpenChange={(v) => !v && setEditingQuote(null)}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-[1000px] p-0 overflow-y-auto"
            data-testid="reviews-editor-sheet"
          >
            <SheetTitle className="sr-only">Édition de la BOM</SheetTitle>
            <QuoteEditor
              quote={editingQuote}
              onClose={() => setEditingQuote(null)}
              onUpdated={() => { setEditingQuote(null); refresh(); toast.success("Nouvelle révision enregistrée"); }}
            />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

function Section({ title, count, accent, className = "", children }) {
  const accentMap = {
    amber: "bg-amber-50 text-amber-900 border-amber-300",
    green: "bg-green-50 text-green-900 border-green-300",
  };
  return (
    <section className={className} data-testid={`reviews-section-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold tracking-[0.18em] uppercase text-zinc-700">{title}</h2>
        <span className={`text-[11px] font-mono font-semibold px-2 py-0.5 rounded-sm border ${accentMap[accent] || ""}`}>
          {count}
        </span>
      </div>
      {children}
    </section>
  );
}

function Empty({ msg }) {
  return (
    <div className="border border-dashed border-zinc-300 rounded-sm py-8 text-center text-sm text-zinc-500" data-testid="reviews-empty">
      {msg}
    </div>
  );
}

function Table({ rows, onChat, onEdit, highlightUnreplied = false, currentUserId }) {
  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
      {/* Mobile / small screens — card layout */}
      <ul className="md:hidden divide-y divide-zinc-100" data-testid="reviews-queue-cards">
        {rows.map((r) => {
          const needsAttention = highlightUnreplied && r.last_message_role === "partner";
          return (
            <li
              key={r.quote_id}
              className={`p-4 ${needsAttention ? "bg-[#D42B1E]/5" : ""}`}
              data-testid={`review-card-${r.quote_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {needsAttention && <span className="h-2 w-2 rounded-full bg-[#D42B1E] animate-pulse shrink-0" />}
                    <p className="font-medium text-zinc-900 truncate">{r.title}</p>
                  </div>
                  <p className="text-xs text-zinc-500 truncate mt-0.5">
                    {r.owner_name || "—"} · {r.owner_company || r.owner_email}
                  </p>
                </div>
                <span className="font-mono text-xs px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 shrink-0">V{r.revision}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-600">
                <span className="font-mono text-zinc-900">{fmt(r.total_cost, r.currency)}</span>
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" /> {r.messages_count}
                </span>
                <span className="font-mono text-[10px] text-zinc-400">{fmtDate(r.review_requested_at)}</span>
              </div>
              <div className="mt-3 flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onChat(r.quote_id)}
                  className="rounded-sm h-8 text-[11px] flex-1"
                  data-testid={`review-open-chat-${r.quote_id}`}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Discuter
                </Button>
                <Button
                  size="sm"
                  onClick={() => onEdit(r.quote_id)}
                  className="rounded-sm h-8 text-[11px] flex-1 bg-[#D42B1E] hover:bg-[#B32218]"
                  data-testid={`review-edit-bom-${r.quote_id}`}
                >
                  <Edit3 className="h-3 w-3 mr-1" />
                  Corriger
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Tablet/desktop — full table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="reviews-queue-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Devis</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 hidden lg:table-cell">Partenaire</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Total</th>
              <th className="px-4 py-2.5 text-center text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Rév.</th>
              <th className="px-4 py-2.5 text-center text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Msg</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 hidden xl:table-cell">Demandé</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const needsAttention = highlightUnreplied && r.last_message_role === "partner";
              return (
                <tr key={r.quote_id}
                  className={`border-b border-zinc-100 hover:bg-zinc-50 ${needsAttention ? "bg-[#D42B1E]/5" : ""}`}
                  data-testid={`review-row-${r.quote_id}`}
                >
                  <td className="px-4 py-3 font-medium text-zinc-900">
                    <div className="flex items-center gap-2">
                      {needsAttention && (
                        <span className="h-2 w-2 rounded-full bg-[#D42B1E] animate-pulse" title="Réponse Fortinet attendue" />
                      )}
                      <span className="truncate">{r.title}</span>
                    </div>
                    <span className="block text-[10px] font-mono text-zinc-400 mt-0.5 lg:hidden">{r.owner_name || r.owner_email}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 hidden lg:table-cell">
                    <div>{r.owner_name || "—"}</div>
                    <span className="text-[11px] text-zinc-500">{r.owner_company || r.owner_email}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-right text-zinc-900 whitespace-nowrap">{fmt(r.total_cost, r.currency)}</td>
                  <td className="px-4 py-3 text-center font-mono text-xs text-zinc-600">V{r.revision}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
                      <MessageSquare className="h-3 w-3" />
                      {r.messages_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs hidden xl:table-cell">{fmtDate(r.review_requested_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onChat(r.quote_id)}
                        className="rounded-sm h-7 text-[11px]"
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Discuter
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => onEdit(r.quote_id)}
                        className="rounded-sm h-7 text-[11px] bg-[#D42B1E] hover:bg-[#B32218]"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Corriger
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
